import { lazy, Suspense, useState } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

import { Button } from '@/components/ui/button';
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/utils/cn';

import { adjacentGlossaryKey, GLOSSARY_TERMS } from './glossary';
import { STAT_LABEL_CLASS } from './HeroStatCard';
import { useReportingGlossary } from './useReportingGlossary';

// KaTeX loads with the first term opened rather than with the app; vite.config.ts keeps it out of the vendor chunk
const GlossaryMarkdown = lazy(() =>
    import('./GlossaryMarkdown').then((module) => ({ default: module.GlossaryMarkdown })),
);

// Bars at the size of a section label and its paragraph (the HeroStatCard loading look) while KaTeX loads.
const MarkdownSkeleton = () => (
    <div className="flex flex-col gap-2 py-2">
        {/* A fixed list that never reorders, and sizes repeat, so the index is the key. */}
        {['h-2.5 w-24', 'h-4 w-full', 'h-4 w-2/3', 'h-2.5 w-40 mt-4', 'h-4 w-full', 'h-4 w-1/2'].map((size, i) => (
            <div key={i} className={cn('bg-cocoa-100 animate-pulse rounded', size)} />
        ))}
    </div>
);

/**
 * The reporting glossary drawer, after the Portfolio Reporting redesign: one term at a time with prev/next, and
 * every term as a chip at the end. Opened by the `glossary` query param (useReportingGlossary), so it is mounted
 * once per page and any metric's info icon can open it.
 */
export const ReportingGlossarySheet = () => {
    const { term, openGlossary, closeGlossary } = useReportingGlossary();
    // Keeps showing the last term while the sheet slides out, after the param is already gone.
    const [shownTerm, setShownTerm] = useState(term);
    if (term && term !== shownTerm) {
        setShownTerm(term);
    }
    const current = term ?? shownTerm;

    return (
        <Sheet open={!!term} onOpenChange={(open) => !open && closeGlossary()}>
            <SheetContent layout="scroll-body" size="sm" aria-describedby={undefined}>
                {current ? (
                    <>
                        <SheetHeader variant="pinned" className="flex-row items-center gap-2 space-y-0">
                            <span className={cn(STAT_LABEL_CLASS, 'flex-1')}>
                                Glossary · {GLOSSARY_TERMS.indexOf(current) + 1} of {GLOSSARY_TERMS.length}
                            </span>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                aria-label="Previous metric"
                                onClick={() => openGlossary(adjacentGlossaryKey(current.key, -1))}
                            >
                                <LuChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                aria-label="Next metric"
                                onClick={() => openGlossary(adjacentGlossaryKey(current.key, 1))}
                            >
                                <LuChevronRight className="h-4 w-4" />
                            </Button>
                        </SheetHeader>
                        {/* Keyed by term so switching terms starts at the top. */}
                        <SheetBody key={current.key} className="flex flex-col gap-5 px-8 py-6">
                            <SheetTitle className="font-heading text-3xl font-normal">{current.name}</SheetTitle>
                            <Suspense fallback={<MarkdownSkeleton />}>
                                <GlossaryMarkdown markdown={current.markdown} />
                            </Suspense>
                            <div className="flex flex-col gap-1.5">
                                <span className={STAT_LABEL_CLASS}>All metrics</span>
                                <div className="flex flex-wrap gap-1">
                                    {GLOSSARY_TERMS.map((chip) => (
                                        <button
                                            key={chip.key}
                                            type="button"
                                            aria-current={chip === current || undefined}
                                            onClick={() => openGlossary(chip.key)}
                                            className={cn(
                                                'text-cocoa-900 hover:bg-muted h-7 rounded-md px-2.5 text-[13px]',
                                                chip === current && 'bg-cocoa-100 font-semibold',
                                            )}
                                        >
                                            {chip.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </SheetBody>
                    </>
                ) : null}
            </SheetContent>
        </Sheet>
    );
};
